import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
	CompanyFeedbackCsvQueryParams,
	createFeedback,
	createFeedbackRequest,
	fetchCompanyFeedbackCsv,
	fetchFeedbackByID,
	fetchFeedbackRequestValid,
	fetchHashtags,
	updateFeedback,
	updateFeedbackRequest,
} from "../../services";
import { PYUseMutationOptions } from "../StandardQueryArguments";

export const useSingleFeedback = (feedbackID: string | undefined, disabled = false) => {
	return useQuery({
		queryKey: ["feedback", feedbackID],
		queryFn: () => (feedbackID ? fetchFeedbackByID(feedbackID) : null),
		meta: {
			errorMessage: "There was an error getting the feedback.",
		},
		enabled: !disabled || !!feedbackID,
	});
};

export const useIsFeedbackRequestValid = (requestedTo: string[] = []) => {
	return useQuery({
		queryKey: ["feedback request valid", requestedTo],
		queryFn: () => fetchFeedbackRequestValid(requestedTo),
		meta: {
			errorMessage: "There was an error getting the feedback.",
		},
	});
};

export const useMutateSingleFeedback = (options?: PYUseMutationOptions) => {
	const client = useQueryClient();
	return useMutation({
		mutationKey: ["feedback"],
		mutationFn: (props: {
			id?: string;
			feedbackRequest?: string;
			aboutList: string[];
			content: string;
			feedback_message: string;
			shareWith: string;
		}) => (props?.id ? updateFeedback(props) : createFeedback(props)),
		...options,
		onSuccess: (data, variables, context) => {
			options?.onSuccess && options.onSuccess(data, variables, context);
			client.invalidateQueries({ queryKey: ["search-service", "feedback"] });
			client.invalidateQueries({ queryKey: ["feedback"] });
		},
	});
};

export const useMutateSingleFeedbackRequest = (options?: PYUseMutationOptions) => {
	const client = useQueryClient();
	return useMutation({
		mutationKey: ["feedback-requests"],
		mutationFn: (props: { id?: string; message?: string; requestedTo?: string[] }) =>
			props?.id
				? updateFeedbackRequest({ message: props.message, id: props.id })
				: createFeedbackRequest({ message: props.message, requestedTo: props.requestedTo }),

		...options,
		onSuccess: (data, variables, context) => {
			options?.onSuccess && options.onSuccess(data, variables, context);
			client.invalidateQueries({ queryKey: ["search-service", "feedback_request"] });
		},
	});
};

export const useCompanyFeedbackCsv = (query: Partial<CompanyFeedbackCsvQueryParams>) => {
	return useQuery({
		queryKey: ["company-feedback-csv", query],
		queryFn: () => fetchCompanyFeedbackCsv(query),
		enabled: false,
		meta: {
			errorMessage: "There was an error getting the company feedback CSV.",
		},
	});
};

export const useCompanyHashtags = () => {
	return useQuery<string[]>({
		queryKey: ["company-hashtags"],
		queryFn: () => fetchHashtags(),
		meta: {
			errorMessage: "There was an error getting the company hashtags",
		},
	});
};
